import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetAnalyticsDashboard,
  GetAnalyticsDashboardVariables,
} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {GET_ANALYTICS_DASHBOARD_QUERY} from 'insights/analytics-dashboards/details/graphql/GetAnalyticsDashboard';
import {
  getAnalyticsDashboardViewStateId,
  MAIN_ANALYTICS_DASHBOARD_VIEW_CONTEXT,
} from 'insights/analytics-dashboards/details/view-state/AnalyticsDashboardViewState';
import {
  areLocalSQLQueriesUnsaved,
  resetLocalSQLQueryOverrides,
} from 'insights/analytics-queries/data/LocalSQLQueryOverride';
import * as navigation from 'Navigation';

import AnalyticsDashboardTabState from './AnalyticsDashboardTabState';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

class AnalyticsDashboardToolbarState extends Backbone.Model {}

interface AnalyticsDashboardFileStateAttributes {
  // These attributes are optional - they may be undefined if dashboard is loading or inaccessible
  analyticsDashboard?: AnalyticsDashboard;
  name?: string;
  url?: string;
}

class AnalyticsDashboardFileState extends ApolloFileState<
  GetAnalyticsDashboard,
  GetAnalyticsDashboardVariables,
  AnalyticsDashboardFileStateAttributes
> {
  static fileType = ModelTypeConstants.ANALYTICS_DASHBOARD;

  private static getViewStateId(fileId: string): string {
    return getAnalyticsDashboardViewStateId(fileId, MAIN_ANALYTICS_DASHBOARD_VIEW_CONTEXT);
  }

  static getQueryOptions(fileId: string): QueryOptions<GetAnalyticsDashboardVariables> {
    return {
      query: GET_ANALYTICS_DASHBOARD_QUERY,
      variables: {
        analyticsDashboardId: fileId,
      },
    };
  }

  static dataToAttributes(data: GetAnalyticsDashboard): AnalyticsDashboardFileStateAttributes {
    const {analyticsDashboard} = data;
    return isInaccessible(analyticsDashboard)
      ? {}
      : {analyticsDashboard, name: analyticsDashboard.name, url: analyticsDashboard.url};
  }

  static openFile(fileId: string): JQueryPromise<AnalyticsDashboardFileState> {
    return super.openFile(fileId) as JQueryPromise<AnalyticsDashboardFileState>;
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new AnalyticsDashboardToolbarState(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    buffers.addTab(
      new AnalyticsDashboardTabState({
        analyticsDashboardId: this.id,
        viewStateId: AnalyticsDashboardFileState.getViewStateId(this.id),
      }),
      {active: true}
    );
  }

  // String if dashboard readable, undefined otherwise (dashboard inaccessible)
  editURL(): string | undefined {
    return this.get('url');
  }

  name(): string {
    return this.get('name') || 'Loading...';
  }

  registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }

  hasUnsavedChanges(): boolean {
    const dashboard = this.get('analyticsDashboard');
    return dashboard != null && areLocalSQLQueriesUnsaved(dashboard);
  }

  cleanUp(): void {
    const dashboard = this.get('analyticsDashboard');
    if (dashboard != null) {
      resetLocalSQLQueryOverrides(dashboard);
    }
    super.cleanUp();
  }
}

export default AnalyticsDashboardFileState;
